/**
 * Browser API base — reads hostMatrix + REACT_APP_* only (no inline host strings).
 *
 * Avoid importing environments.js here (webpack circular init with appConfig).
 *
 * ***** Code Start BH28082026_1115 - Sticky absolute API fallback on IIS/SPA misroutes
 * IIS SPA hosts often answer /api/* with 406 (Accept: application/json) or HTML.
 * Detect that once, pin the matrix LLM origin for the tab, and retry.
 */
import hostMatrix from '../config/candor_host_matrix.json';

const STICKY_SESSION_KEY = 'candor_api_base_override';

/** Prefer JSON; include wildcard Accept so IIS content-negotiation does not hard-406. */
export const API_JSON_ACCEPT = 'application/json, text/plain;q=0.9, */*;q=0.8';

function trimSlash(url) {
  return String(url || '')
    .trim()
    .replace(/\/$/, '');
}

/** Production SPA hosts (matrix production row + www twin). */
function productionFrontendHosts() {
  const row = (hostMatrix.environments || {}).production || {};
  const hosts = new Set();
  [row.frontend_origin, row.dashboard_url].forEach((u) => {
    try {
      const h = new URL(u).hostname.toLowerCase();
      if (!h) return;
      hosts.add(h);
      hosts.add(h.startsWith('www.') ? h.slice(4) : `www.${h}`);
    } catch (_) {}
  });
  return hosts;
}

/** True on the production website — never follow saved QA/Dev targets there. */
export function isProductionFrontendHost() {
  if (typeof window === 'undefined') return false;
  const h = (window.location.hostname || '').toLowerCase();
  return productionFrontendHosts().has(h);
}

export function readRuntimeEnv() {
  if (typeof window === 'undefined') return '';
  // A leftover candor_target_env (qa/dev) from testing pointed prod at QA/Dev APIs
  // ("Cannot reach the sign-in service"). Production hosts always use the build env.
  if (isProductionFrontendHost()) return '';
  try {
    const hostname = (window.location.hostname || '').toLowerCase();
    if (hostname.includes('ultradocuments') || hostname.includes('candorqa')) return 'qa';
    if (hostname.includes('candordev')) return 'dev_server';

    const params = new URLSearchParams(window.location.search);
    const p = (params.get('env') || params.get('api') || params.get('target') || '').toLowerCase();
    if (params.has('qa') || p === 'qa' || p === 'staging') {
      sessionStorage.setItem('candor_target_env', 'qa');
      return 'qa';
    }
    if (params.has('dev') || p === 'dev' || p === 'dev_server' || p === 'devserver') {
      sessionStorage.setItem('candor_target_env', 'dev_server');
      return 'dev_server';
    }

    const ref = (document.referrer || '').toLowerCase();
    if (ref.includes('ultradocuments.com') || ref.includes('candorqa.candorsys.com')) return 'qa';
    if (ref.includes('candordev.candorsys.com')) return 'dev_server';

    const stored = (
      sessionStorage.getItem('candor_target_env') ||
      localStorage.getItem('candor_target_env') ||
      ''
    ).toLowerCase();
    if (stored === 'qa') return 'qa';
    if (stored === 'dev_server' || stored === 'dev') return 'dev_server';
  } catch (_) {}
  return '';
}

function appEnv() {
  const runtime = readRuntimeEnv();
  if (runtime) return runtime;
  const raw = String(process.env.REACT_APP_ENVIRONMENT || '')
    .trim()
    .toLowerCase();
  if (raw === 'qa' || raw === 'staging' || raw === 'stage') return 'qa';
  if (raw === 'production' || raw === 'prod') return 'production';
  if (raw === 'dev_server' || raw === 'devserver') return 'dev_server';
  if (raw === 'development' || raw === 'dev' || raw === 'local') return 'development';
  if (process.env.NODE_ENV === 'production') return 'production';
  return 'development';
}

function matrixLlm(envName) {
  const row = (hostMatrix.environments || {})[envName] || {};
  return trimSlash(row.llm_api_url || '');
}

function fallbackBase() {
  const env = appEnv();
  if (env === 'qa') return matrixLlm('qa');
  if (env === 'production') return matrixLlm('production');
  if (env === 'dev_server') return matrixLlm('dev_server');
  // development: empty so setupProxy can forward /api
  return '';
}

/** Always-absolute matrix LLM origin for the active env (incl. local DEV). */
export function absoluteMatrixApiBaseUrl() {
  const env = appEnv();
  if (env === 'qa') return matrixLlm('qa');
  if (env === 'production') return matrixLlm('production');
  if (env === 'dev_server') return matrixLlm('dev_server');
  return matrixLlm('development');
}

let _memorySticky = '';

function readSticky() {
  let v = _memorySticky;
  if (!v) {
    try {
      if (typeof sessionStorage === 'undefined') return '';
      v = trimSlash(sessionStorage.getItem(STICKY_SESSION_KEY) || '');
    } catch (_) {
      return '';
    }
  }
  // Production hosts only honor a pin to the production LLM API.
  if (v && isProductionFrontendHost() && v !== matrixLlm('production')) {
    _memorySticky = '';
    try {
      sessionStorage.removeItem(STICKY_SESSION_KEY);
    } catch (_) {}
    return '';
  }
  if (v) _memorySticky = v;
  return v;
}

/** Pin API origin for this tab after a confirmed SPA/IIS misroute. */
export function pinApiBaseUrl(base) {
  const b = trimSlash(base);
  if (!b) return;
  _memorySticky = b;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(STICKY_SESSION_KEY, b);
    }
  } catch (_) {
    /* private mode */
  }
}

/**
 * Dynamically set active target environment (for cantest/dev/qa testing).
 * @param {'qa'|'dev_server'|'dev'} env
 */
export function setRuntimeTargetEnvironment(env) {
  if (typeof window === 'undefined') return;
  try {
    const normalized = (env || '').toLowerCase();
    if (normalized === 'qa') {
      sessionStorage.setItem('candor_target_env', 'qa');
      localStorage.setItem('candor_target_env', 'qa');
      sessionStorage.removeItem(STICKY_SESSION_KEY);
      _memorySticky = '';
    } else if (normalized === 'dev_server' || normalized === 'dev') {
      sessionStorage.setItem('candor_target_env', 'dev_server');
      localStorage.setItem('candor_target_env', 'dev_server');
      sessionStorage.removeItem(STICKY_SESSION_KEY);
      _memorySticky = '';
    }
  } catch (_) {}
}

/** @returns {string} Absolute API origin, or '' for same-origin proxy */
export function resolveApiBaseUrl() {
  const sticky = readSticky();
  if (sticky) return sticky;

  // Runtime environment override on cantest/dev/qa hosts
  const runtime = readRuntimeEnv();
  if (runtime === 'qa') return matrixLlm('qa');
  if (runtime === 'dev_server') return matrixLlm('dev_server');

  const fromEnv = process.env.REACT_APP_API_BASE_URL;
  if (fromEnv !== undefined && String(fromEnv).trim() !== '') {
    return trimSlash(fromEnv);
  }
  return fallbackBase();
}

/**
 * Join a path with the resolved API base.
 * @param {string} path e.g. '/api/v1/auth/login'
 * @returns {string}
 */
export function apiUrl(path) {
  const raw = path == null ? '' : String(path);
  const p = raw.startsWith('/') ? raw : raw ? `/${raw}` : '';
  const base = resolveApiBaseUrl();
  if (!base) {
    return p || '/';
  }
  return `${base.replace(/\/$/, '')}${p}`;
}

function normalizeApiPath(path) {
  const raw = path == null ? '' : String(path);
  return raw.startsWith('/') ? raw : raw ? `/${raw}` : '/';
}

/**
 * True when the response is from an SPA/static host instead of the LLM API.
 * @param {Response} res
 * @param {string} [bodyText]
 */
export function isApiMisrouteResponse(res, bodyText) {
  if (!res) return false;
  if (res.status === 406 || res.status === 405) return true;
  const ct = String(res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('text/html')) return true;
  if (typeof bodyText === 'string' && /^\s*<(!doctype\s+html|html\b)/i.test(bodyText)) {
    return true;
  }
  return false;
}

function sameOriginUrl(a, b) {
  try {
    const left = new URL(a, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
    const right = new URL(b, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
    return left.origin === right.origin && left.pathname === right.pathname;
  } catch (_) {
    return a === b;
  }
}

export function getAlternativeTestApi(currentBase) {
  const devUrl = matrixLlm('dev_server');
  const qaUrl = matrixLlm('qa');
  const cur = trimSlash(currentBase || resolveApiBaseUrl());
  if (cur.includes('candordev')) return { url: qaUrl, env: 'qa' };
  if (cur.includes('ultradocuments') || cur.includes('candorqa')) return { url: devUrl, env: 'dev_server' };
  return { url: qaUrl, env: 'qa' };
}

/**
 * fetch() with automatic retry against the matrix absolute API when the
 * primary URL looks like an IIS/SPA misroute (406 / HTML) or is down on test hosts.
 * @param {string} path API path starting with /api/...
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export async function apiFetch(path, init = {}) {
  const p = normalizeApiPath(path);
  const headers = new Headers(init.headers || {});
  if (!headers.has('Accept')) {
    headers.set('Accept', API_JSON_ACCEPT);
  }
  const nextInit = { ...init, headers };

  const primaryUrl = apiUrl(p);
  let res;
  try {
    res = await fetch(primaryUrl, nextInit);
  } catch (err) {
    if (typeof window !== 'undefined') {
      const h = (window.location.hostname || '').toLowerCase();
      if (h === 'cantest.serveftp.com' || h === 'localhost' || h === '127.0.0.1') {
        const alt = getAlternativeTestApi(primaryUrl);
        if (alt && alt.url && !sameOriginUrl(primaryUrl, `${alt.url}${p}`)) {
          pinApiBaseUrl(alt.url);
          setRuntimeTargetEnvironment(alt.env);
          return fetch(`${alt.url}${p}`, nextInit);
        }
      }
    }
    throw err;
  }

  let peek = '';
  try {
    peek = await res.clone().text();
  } catch (_) {
    peek = '';
  }

  if (!isApiMisrouteResponse(res, peek)) {
    return res;
  }

  const abs = absoluteMatrixApiBaseUrl();
  if (!abs) {
    return res;
  }
  const absUrl = `${abs}${p}`;
  if (sameOriginUrl(primaryUrl, absUrl)) {
    return res;
  }

  pinApiBaseUrl(abs);
  return fetch(absUrl, nextInit);
}

// ***** Code End BH28082026_1115

const apiBase = {
  resolveApiBaseUrl,
  apiUrl,
  apiFetch,
  pinApiBaseUrl,
  absoluteMatrixApiBaseUrl,
  isApiMisrouteResponse,
  API_JSON_ACCEPT,
};
export default apiBase;
